'use client'
import {
  useFilterState
} from "./chunk-TOSXVIFC.mjs";
import {
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/hooks/useFilterQuery.ts
import { useMemo } from "react";
import {
  dateAdd,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isArabic,
  startOfDay,
  startOfMonth,
  startOfWeek
} from "@wbk/utils";
import { useRouter } from "@wbk/config";
var useFilterQuery = ({ state: predefined } = {}) => {
  const { state: urlState } = useFilterState();
  const { locale } = useRouter();
  const state = __spreadValues(__spreadValues({}, urlState), predefined);
  const lang = state.search ? isArabic(state.search) ? "ar" : "en" : locale;
  const organizerQuery = useMemo(() => {
    var _a;
    if ((_a = state.organizer) == null ? void 0 : _a.length) {
      return {
        OR: [
          {
            organizer: {
              slug_in: state.organizer
            }
          }
        ]
      };
    }
    return {};
  }, [state.organizer]);
  const categoryQuery = useMemo(() => {
    if (state.category) {
      return {
        OR: [{ category: { slug: state.category } }]
      };
    }
    return {};
  }, [state.category]);
  const subcategoryQuery = useMemo(() => {
    if (state.subcategory) {
      return {
        OR: [{ subCategory: { slug: state.subcategory } }]
      };
    }
    return {};
  }, [state.subcategory]);
  const tagQuery = useMemo(() => {
    if (state.tag) {
      return {
        OR: [{ tags: { slug: state.tag } }]
      };
    }
    return {};
  }, [state.tag]);
  const priceQuery = useMemo(() => {
    const from = Number(state.minPrice);
    const to = Number(state.maxPrice);
    const valid = typeof from === "number" && from >= 0;
    if (valid) {
      const safeTo = to > from ? to : from;
      return {
        AND: [{ price_gte: from }, { price_lte: safeTo }]
      };
    }
    return {};
  }, [state.maxPrice, state.minPrice]);
  const sortQuery = useMemo(() => {
    switch (state.sort) {
      case "newest":
        return "sys_publishedAt_DESC";
      case "oldest":
        return "sys_publishedAt_ASC";
      case "highest_price":
        return "price_DESC";
      case "lowest_price":
        return "price_ASC";
      default:
        return ["tier_ASC", "order_ASC"];
    }
  }, [state.sort]);
  const searchQuery = useMemo(() => {
    if (state.search) {
      return {
        OR: [
          { title_contains: state.search },
          { description_contains: state.search },
          { category: { title_contains: state.search } },
          {
            seo: {
              OR: [
                { title_contains: state.search },
                { keywords_contains: state.search },
                { description_contains: state.search }
              ]
            }
          }
        ]
      };
    }
    return {};
  }, [state.search]);
  const dateQuery = useMemo(() => {
    let from;
    let to;
    if (state.from && state.to) {
      from = startOfDay(new Date(state.from));
      to = endOfDay(new Date(state.to));
    } else if (state.date === "today") {
      from = startOfDay(/* @__PURE__ */ new Date());
      to = endOfDay(/* @__PURE__ */ new Date());
    } else if (state.date === "tomorrow") {
      const tomorrowStart = dateAdd({ date: /* @__PURE__ */ new Date(), interval: "day", value: 1 });
      const tomorrowEnd = dateAdd({ date: /* @__PURE__ */ new Date(), interval: "day", value: 1 });
      if (tomorrowStart && tomorrowEnd) {
        from = startOfDay(tomorrowStart);
        to = endOfDay(tomorrowEnd);
      }
    } else if (state.date === "thisweek") {
      from = startOfDay(startOfWeek(/* @__PURE__ */ new Date()));
      to = endOfDay(endOfWeek(/* @__PURE__ */ new Date()));
    } else if (state.date === "thismonth") {
      from = startOfMonth(/* @__PURE__ */ new Date());
      to = endOfMonth(/* @__PURE__ */ new Date());
    }
    if (from && to) {
      return {
        OR: [
          {
            schedule: {
              openDateTime_lte: to,
              closeDateTime_gte: from
            }
          },
          {
            schedule: {
              openDateTime_gte: from,
              openDateTime_lte: to
            }
          },
          {
            schedule: {
              openDateTime_lte: to,
              closeDateTime_exists: false
            }
          }
        ]
      };
    }
    return {};
  }, [state.date, state.from, state.to]);
  const temperatureQuery = useMemo(() => {
    if (state.minTemp && state.maxTemp) {
      return {
        OR: [
          {
            location: {
              minimumSuitableTemperature_lte: state.maxTemp,
              maximumSuitableTemperature_gte: state.minTemp
            }
          },
          {
            location: {
              minimumSuitableTemperature_gte: state.minTemp,
              minimumSuitableTemperature_lte: state.maxTemp
            }
          },
          {
            location: {
              maximumSuitableTemperature_lte: state.maxTemp,
              minimumSuitableTemperature_exists: false
            }
          },
          {
            location: {
              maximumSuitableTemperature_exists: false,
              minimumSuitableTemperature_lte: state.maxTemp
            }
          }
        ]
      };
    }
    return {};
  }, [state.minTemp, state.maxTemp]);
  const query = useMemo(() => {
    return {
      lang,
      limit: state.perPage,
      skip: (Number(state.page) - 1) * state.perPage,
      order: sortQuery,
      where: {
        AND: [
          organizerQuery,
          categoryQuery,
          subcategoryQuery,
          tagQuery,
          priceQuery,
          searchQuery,
          dateQuery,
          temperatureQuery
        ]
      }
    };
  }, [
    lang,
    state.perPage,
    state.page,
    sortQuery,
    tagQuery,
    organizerQuery,
    categoryQuery,
    subcategoryQuery,
    priceQuery,
    searchQuery,
    dateQuery,
    temperatureQuery
  ]);
  return { query };
};
var useFilterQuery_default = useFilterQuery;

export {
  useFilterQuery_default
};
//# sourceMappingURL=chunk-7GAGCG6W.mjs.map